import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import styled, { keyframes as kf } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt,
  faBriefcase,
  faAt,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Logo from '../components/Logo/Logo';
import Header from '../components/Header/Header';
import Navigation from '../components/Navigation/Navigation';
import Footer from '../components/Footer/Footer';
import API from '../api';
import { API_RESPONSE_TYPES } from '../constants';
import { authData } from '../utils';
import ScrollMessage from '../components/ScrollMessage/ScrollMessage';

const StyledMain = styled.main`
  width: 95%;
  max-width: 1800px;
  height: auto;
  margin: 0 auto;
  padding: 0px;
`;

const StyledContantBlocksSection = styled.section`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  row-gap: 25px;
  column-gap: 25px;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  margin: 0px;
  padding: 0px;
  @media (max-width: 1000px) {
    display: block;
    width: 92%;
    margin: 0 auto;
  }
`;

const StyledContentBlock = styled.section`
  width: calc(33.33% - 20px);
  min-height: 150px;
  height: auto;
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : '#0095ff'};
  border-radius: 15px;
  box-shadow: 0px 0px 50px -10px #d1d1d1;
  @media (max-width: 1000px) {
    width: 100%;
    margin: 15px auto;
  }
`;

const StyledContentHeading = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #ffffff;
  margin: 20px 20px 10px;
  padding: 0px;
  @media (max-width: 1000px) {
    margin: 0px;
    padding: 15px 15px 0px;
  }
`;

const StyledContentPara = styled.p`
  font-size: 14px;
  color: ${({ color }) => (color ? color : '#ffffff')};
  margin: 0px 20px 20px;
  padding: 0px;
  line-height: 24px;
  @media (max-width: 1000px) {
    margin: 0px;
    padding: 15px 15px 0px 15px;
  }
`;

const StyledContactSection = styled.section`
  width: calc(100% - 50px);
  height: auto;
  padding: 0px;
  margin: 10px 0px 0px 15px;
`;

const StyledAddressContent = styled.section``;

const StyledPhoneContent = styled.section`
  display: flex;
  flex-direction: row;
  column-gap: 15px;
  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const StyledContactInfo = styled.section`
  height: auto;
  display: flex;
  flex-direction: row;
  column-gap: 15px;
  margin-bottom: 5px;
`;

const StyledIcon = styled.section`
  font-size: 25px;
  color: #545454;
`;

const StyledContent = styled.section`
  h3 {
    font-size: 16px;
    color: #545454;
    margin: 0px 0px 10px 0px;
    padding: 0px;
    font-weight: normal;
  }
  address {
    font-size: 14px;
    color: #545454;
    font-weight: normal;
    font-style: normal;
    line-height: 24px;
  }
`;

const StyledNumberContent = styled.section`
  align-items: center;
  display: flex;
  a {
    font-size: 14px;
    text-decoration: none;
    color: #0095ff;
  }
`;

const StyledContactUsHeading = styled.h1`
  font-size: 24px;
  font-weight: normal;
  margin: 25px 15px 0px;
  padding: 0px;
  color: #000000;
  font-family: math;
`;

const StyledAboutUsText = styled.p`
  font-size: 14px;
  line-height: 24px;
  text-align: justify;
  margin: 10px 15px;
  color: #545454;
`;


const gradientAnimation = (primary = '#150080', secondary = '#8000ff') => kf`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const StyledAuthMainOuter = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  justify-content: center;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px 0;
  }
`;


const StyledMainSection = styled.div`
  display: flex;
  max-width: 900px;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.3);
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StyledAuthFormSection = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  padding: 60px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const StyledAuthHeader = styled.div`
  margin-bottom: 40px;
  text-align: center;
  img {
    transition: transform 0.3s ease;
    &:hover {
      transform: scale(1.1);
    }
  }
`;

const StyledAuthFormIn = styled.div`
  width: 100%;
`;

const StyledGradientPanel = styled.div`
  flex: 1;
  background: linear-gradient(-45deg, ${(props) => props.primary}, ${(props) => props.secondary});
  background-size: 400% 400%;
  animation: ${(props) => gradientAnimation(props.primary, props.secondary)} 15s ease infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(255,255,255,0.1);
  @media (max-width: 768px) {
    display: none;
  }
`;

const APKLink = styled.a`
  margin-top: 25px;
  display: inline-block;
  img {
    width: 130px;
    border-radius: 12px;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 8px 20px rgba(0,0,0,0.3);
    }
  }
`;



const withAuthHOC = (WrappedComponent) => {
  return (props) => {
    const {
      appName,
      appLogo,
      landingPrimaryColor = '#150080',
      landingSecondaryColor = '#8000ff',
      apkLink,
    } = props.appData;

    return (
      <StyledAuthMainOuter>
        <StyledMainSection>
          <StyledAuthFormSection>
            <StyledAuthHeader>
              <Logo src={appLogo} alt={appName} type="auth" />
            </StyledAuthHeader>

            <StyledAuthFormIn>
              <WrappedComponent {...props} />
            </StyledAuthFormIn>

            {apkLink && (
              <APKLink href={apkLink} target="_blank" title="Download Android APK">
                <img src="/apk-download.png" alt="Download APK" />
              </APKLink>
            )}
          </StyledAuthFormSection>

          <StyledGradientPanel
            primary={landingPrimaryColor}
            secondary={landingSecondaryColor}
          />
        </StyledMainSection>
      </StyledAuthMainOuter>
    );
  };
};


const withAppHOC = (WrappedComponent) => {
  return (props) => {
    const { accountData } = authData.get();
    const {
      appName,
      appLogo,
      appPhoneNumber,
      appEmailAddress,
      packageExpiryDate,
    } = props.appData;
    const [showNav, setShowNav] = useState(false);
    const [walletBalance, setWalletBalance] = useState('-');
    const [creditDue, setCreditDue] = useState('-');
    const [minimumBalance, setMinimumBalance] = useState('-');
    const [profileName, setProfileName] = useState('-');
    const [profilePhoto, setProfilePhoto] = useState('');
    const [scrollMessage, setScrollMessage] = useState('');
    const [fcmToken, setFcmToken] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [authDomain, setAuthDomain] = useState('');
    const [projectId, setProjectId] = useState('');
    const [storageBucket, setStorageBucket] = useState('');
    const [messagingSenderId, setMessagingSenderId] = useState('');
    const [appId, setAppId] = useState('');
    const [measurementId, setMeasurementId] = useState('');
    const [vapidKey, setVapidKey] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [accountsRole, setAccountsRole] = useState('');

    const onClickLogout = (e) => {
      e.preventDefault();
      const { authToken } = authData.get();
      API.delete(`/auth/sessions/?sessionToken=${authToken}`);
      authData.clear();
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
      window.location.href = '/';
    };

    const saveFcm = useCallback(() => {
      API.post(`/auth/save-fcm`, { fcmToken });
    }, [fcmToken]);

    const readNotifications = useCallback(() => {
      API.get(`/configurations/notifications`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setNotifications(data);
        }
      });
    }, []);

    const updateNotification = (id) => {
      API.put(`/configurations/notifications/${id}`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setNotifications(data);
        }
      });
    };

    const readWalletBalance = useCallback(() => {
      API.get(`/auth/profile`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          authData.set('accountData', JSON.stringify(data));
          authData.set('accountsPermissions', data?.permissions);
          setWalletBalance(data.walletBalance);
          setCreditDue(data.creditDue);
          setMinimumBalance(data.minimumBalance);
          setProfileName(`${data.firstName} ${data.lastName}` || 'N/A');
          setProfilePhoto(data.photo || '');
          setAccountsRole(data.accountsRole || '');
        }
      });
    }, []);

    const readScrollMessage = useCallback(() => {
      API.get(`/configurations/scroll-message`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setScrollMessage(data);
        }
      });
    }, []);

    const onClickNavigationIcon = () => {
      setShowNav((showNav) => !showNav);
    };

    const readPublicFCMWebConfig = useCallback(() => {
      API.get(`/configurations/push-notifications-configuration/public`).then(
        (response) => {
          const { status, data } = response.data;
          if (status === API_RESPONSE_TYPES.SUCCESS) {
            const {
              apiKey,
              authDomain,
              projectId,
              storageBucket,
              messagingSenderId,
              appId,
              measurementId,
              vapidKey,
            } = data;
            setApiKey(apiKey ? apiKey : '');
            setAuthDomain(authDomain ? authDomain : '');
            setProjectId(projectId ? projectId : '');
            setStorageBucket(storageBucket ? storageBucket : '');
            setMessagingSenderId(messagingSenderId ? messagingSenderId : '');
            setAppId(appId ? appId : '');
            setMeasurementId(measurementId ? measurementId : '');
            setVapidKey(vapidKey ? vapidKey : '');
          }
        }
      );
    }, []);

    const initFirebase = useCallback(async () => {
      const app = await initializeApp({
        apiKey,
        authDomain,
        projectId,
        storageBucket,
        messagingSenderId,
        appId,
        measurementId,
      });

      const messaging = getMessaging(app);

      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.info('Notification User Permission Granted.');
          return getToken(messaging, {
            vapidKey,
          })
            .then((currentToken) => {
              if (currentToken) {
                console.info('FCM Token: ', currentToken);
                if (fcmToken !== currentToken) {
                  setFcmToken(currentToken);
                }
              } else {
                console.info('Failed to generate FCM token.');
              }
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          console.info('Notification User Permission Denied.');
        }
      });

      onMessage(
        messaging,
        (payload) => {
          console.info(payload);
          readNotifications();
        },
        (error) => {
          console.error(error);
        }
      );

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .register('/firebase-messaging-sw.js', {
            apiKey,
            authDomain,
            projectId,
            storageBucket,
            messagingSenderId,
            appId,
            measurementId,
          })
          .then((registration) => {
            console.info(
              'Registration successful, scope is:',
              registration.scope
            );
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }, [
      fcmToken,
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
      vapidKey,
      readNotifications,
    ]);

    useEffect(() => {
      readWalletBalance();
      readScrollMessage();
      readPublicFCMWebConfig();
      readNotifications();
    }, [
      readWalletBalance,
      readScrollMessage,
      readPublicFCMWebConfig,
      readNotifications,
    ]);

    useEffect(() => {
      if (
        apiKey !== '' &&
        authDomain !== '' &&
        projectId !== '' &&
        storageBucket !== '' &&
        messagingSenderId !== '' &&
        appId !== '' &&
        measurementId !== '' &&
        vapidKey !== ''
      ) {
        initFirebase();
      }
    }, [
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
      vapidKey,
      initFirebase,
    ]);

    useEffect(() => {
      saveFcm();
    }, [fcmToken, saveFcm]);

    return (
      <>
        <Header
          walletBalance={walletBalance}
          creditDue={creditDue}
          minimumBalance={minimumBalance}
          supportMobileNumber={appPhoneNumber}
          supportEmailAddress={appEmailAddress}
          fullName={profileName}
          profilePhoto={profilePhoto}
          onClickLogout={onClickLogout}
          appLogo={appLogo}
          appName={appName}
          packageExpiryDate={packageExpiryDate}
          showNav={showNav}
          setShowNav={onClickNavigationIcon}
          notifications={notifications}
          updateNotification={updateNotification}
          commissionMode={accountData.commissionMode}
          commissionValue={accountData.commissionValue}
          accountsRole={accountsRole}
        />
        <Navigation showNav={showNav} />
        <ScrollMessage scrollMessage={scrollMessage} />
        <StyledMain>
          <WrappedComponent {...props} readWalletBalance={readWalletBalance} />
        </StyledMain>
        <Footer appName={appName} />
      </>
    );
  };
};

export { withAuthHOC, withAppHOC };
